<template>
  <div class="search-page">
    <site-header />

    <search-hero
      :keyword="filters.keyword"
      :category="filters.category"
      :same-name-multi="filters.sameNameMulti"
      @search="onHeroSearch"
      @industry-select="onFilterPatch"
    />

    <main class="search-page__main">
      <div class="core-container search-page__stack">
        <search-filter-panel
          :filters="filters"
          @change="onFilterPatch"
          @clear="onClearFilters"
        />

        <trademark-grid
          ref="trademarkGrid"
          title="精品商标推荐"
          filter-options-source="search"
          toolbar-mode="full"
          :page-size="searchPageSize"
          :auto-load="false"
          embed-mode
          @filters-change="onGridFiltersChange"
        />
      </div>
    </main>

    <site-footer />
  </div>
</template>

<script>
import SiteHeader from '@/components/home/SiteHeader.vue'
import SiteFooter from '@/components/home/SiteFooter.vue'
import TrademarkGrid from '@/components/home/TrademarkGrid.vue'
import SearchHero from '@/components/search/SearchHero.vue'
import SearchFilterPanel from '@/components/search/SearchFilterPanel.vue'
import {
  SEARCH_PAGE_SIZE,
  createEmptySearchFilters
} from '@/constants/searchPage'

export default {
  name: 'TrademarkSearchPage',
  components: {
    SiteHeader,
    SiteFooter,
    TrademarkGrid,
    SearchHero,
    SearchFilterPanel
  },
  data() {
    return {
      filters: createEmptySearchFilters(),
      searchPageSize: SEARCH_PAGE_SIZE,
      routeSelfUpdate: false
    }
  },
  watch: {
    '$route.query': {
      immediate: false,
      handler() {
        this.syncFromRoute()
        if (this.routeSelfUpdate) return
        this.reloadList()
      }
    }
  },
  mounted() {
    this.syncFromRoute()
    this.reloadList()
  },
  methods: {
    syncFromRoute() {
      const query = this.$route.query
      const next = createEmptySearchFilters()

      if (query.keyword) next.keyword = String(query.keyword)
      if (query.search_type) next.searchType = Number(query.search_type) || 1
      if (query.category) next.category = String(query.category)
      if (query.group) next.group = String(query.group)
      if (query.same_name_flag === '1') next.sameNameMulti = true
      if (query.status) next.status = String(query.status)
      if (query.price) next.price = String(query.price)
      if (query.price_min) next.priceMin = String(query.price_min)
      if (query.price_max) next.priceMax = String(query.price_max)
      if (query.sort) next.sort = String(query.sort)
      if (query.active_sort) next.activeSort = String(query.active_sort)

      this.filters = next
    },
    buildRouteQuery() {
      const query = {}
      const filters = this.filters

      if (filters.keyword) query.keyword = filters.keyword
      if (filters.searchType && filters.searchType !== 1) {
        query.search_type = String(filters.searchType)
      }
      if (filters.category) query.category = filters.category
      if (filters.group) query.group = filters.group
      if (filters.sameNameMulti) query.same_name_flag = '1'
      if (filters.status) query.status = filters.status
      if (filters.price) query.price = filters.price
      if (filters.priceMin) query.price_min = filters.priceMin
      if (filters.priceMax) query.price_max = filters.priceMax
      if (filters.sort && filters.sort !== 'default') query.sort = filters.sort
      if (filters.activeSort && filters.activeSort !== 'default') {
        query.active_sort = filters.activeSort
      }

      return query
    },
    updateRoute() {
      const nextQuery = this.buildRouteQuery()
      const currentQuery = this.$route.query
      const same =
        JSON.stringify(nextQuery) === JSON.stringify(currentQuery)

      if (same) return

      this.routeSelfUpdate = true
      this.$router.replace({
        path: '/search',
        query: nextQuery
      })
        .catch(() => {})
        .finally(() => {
          this.$nextTick(() => {
            this.routeSelfUpdate = false
          })
        })
    },
    reloadList() {
      const grid = this.$refs.trademarkGrid
      if (!grid) return

      grid.applySearch({
        keyword: this.filters.keyword,
        searchType: this.filters.searchType,
        category: this.filters.category,
        group: this.filters.group,
        sameNameMulti: this.filters.sameNameMulti,
        status: this.filters.status,
        price: this.filters.price,
        priceMin: this.filters.priceMin,
        priceMax: this.filters.priceMax,
        sort: this.filters.sort,
        activeSort: this.filters.activeSort
      })
    },
    onHeroSearch(payload) {
      this.filters = {
        ...this.filters,
        keyword: payload.keyword
      }
      this.updateRoute()
      this.reloadList()
    },
    onFilterPatch(patch) {
      this.filters = {
        ...this.filters,
        ...patch
      }
      this.updateRoute()
      this.reloadList()
    },
    onClearFilters() {
      this.filters = createEmptySearchFilters()
      this.updateRoute()
      this.reloadList()
    },
    onGridFiltersChange(payload) {
      this.filters = {
        ...createEmptySearchFilters(),
        ...payload
      }
      this.updateRoute()
    }
  }
}
</script>

<style scoped>
.search-page {
  min-height: 100vh;
  background: var(--color-bg-main);
}

.search-page__main {
  padding: var(--space-lg) 0 var(--space-xl);
}

.search-page__stack {
  display: flex;
  flex-direction: column;
  gap: var(--search-stack-gap);
}
</style>
